// ---------------------------------------------------------------------------
// Campaigns data layer (OPUS-D04)
// ---------------------------------------------------------------------------
// TanStack Query hooks for the campaign-dashboard read model.
// Contracts mirror apps/api/src/routes/campaign-dashboard.ts (feature #62).
// ---------------------------------------------------------------------------

import { useQuery, type UseQueryResult } from "@tanstack/react-query";

// ---- domain types (shape-matched to the api router) ----------------------

export const CAMPAIGN_STATES = [
	"draft",
	"active",
	"paused",
	"archived",
] as const;
export type CampaignState = (typeof CAMPAIGN_STATES)[number];

/**
 * UI sometimes says "inactive" and "live" per feature #61 vocabulary; we map
 * the api states onto a badge tone. Archived is shown via a neutral zinc.
 */
export const STATE_BADGE_TONE: Record<
	CampaignState,
	"emerald" | "sky" | "amber" | "zinc"
> = {
	active: "emerald",
	draft: "sky",
	paused: "amber",
	archived: "zinc",
};

/**
 * Human-facing label for each state. Keeps the table copy stable so we do not
 * leak raw api enums into the UI.
 */
export const STATE_LABEL: Record<CampaignState, string> = {
	draft: "Draft",
	active: "Live",
	paused: "Paused",
	archived: "Archived",
};

export interface CampaignRow {
	id: string;
	tenant_id: string;
	name: string;
	vertical: string;
	state: CampaignState;
	clicks_count: number;
	conversions_count: number;
	revenue_minor: number;
	currency: string;
	updated_at: number;
}

export interface ListResult {
	items: CampaignRow[];
	next_cursor: string | null;
}

export interface ListFilters {
	state?: CampaignState | "all";
	q?: string;
	cursor?: string;
	limit?: number;
}

// ---- base url resolution --------------------------------------------------

/**
 * Resolves the api base url for the dashboard. The dash app is served from
 * port 5173 in local dev; the api runs on 8787. In prod the dash bundle is
 * served from the same origin as the api, so we prefer that when it exists.
 */
export function resolveApiBase(): string {
	const envBase =
		typeof import.meta !== "undefined" &&
		(import.meta as unknown as { env?: Record<string, string> }).env?.[
			"VITE_API_BASE"
		];
	if (envBase && envBase.length > 0) return envBase.replace(/\/$/, "");
	if (typeof window !== "undefined") {
		const { hostname, protocol } = window.location;
		// localhost or 127.0.0.1 → talk to the local api server on :8787
		if (hostname === "localhost" || hostname === "127.0.0.1") {
			return `${protocol}//${hostname}:8787`;
		}
	}
	return "";
}

// ---- fetcher --------------------------------------------------------------

export async function fetchCampaignList(
	filters: ListFilters,
	signal?: AbortSignal,
): Promise<ListResult> {
	const params = new URLSearchParams();
	if (filters.state && filters.state !== "all") params.set("state", filters.state);
	if (filters.q && filters.q.length > 0) params.set("q", filters.q);
	if (filters.cursor) params.set("cursor", filters.cursor);
	params.set("limit", String(filters.limit ?? 25));

	const base = resolveApiBase();
	const url = `${base}/api/v1/campaign-dashboard/list?${params.toString()}`;
	const res = await fetch(url, {
		method: "GET",
		signal,
		headers: { Accept: "application/json" },
		credentials: "include",
	});
	if (!res.ok) {
		const text = await res.text().catch(() => "");
		throw new Error(
			`campaigns.list failed (${res.status}): ${text || res.statusText}`,
		);
	}
	const json = (await res.json()) as ListResult;
	// Be defensive — the api never returns nulls here but we want a crisp UI
	// if the contract drifts.
	return {
		items: Array.isArray(json.items) ? json.items : [],
		next_cursor: json.next_cursor ?? null,
	};
}

// ---- react-query hook -----------------------------------------------------

export const CAMPAIGNS_LIST_QUERY_KEY = ["campaigns", "list"] as const;

export function useCampaignsList(
	filters: ListFilters,
): UseQueryResult<ListResult, Error> {
	return useQuery<ListResult, Error>({
		queryKey: [
			...CAMPAIGNS_LIST_QUERY_KEY,
			filters.state ?? "all",
			filters.q ?? "",
			filters.cursor ?? "",
			filters.limit ?? 25,
		],
		queryFn: ({ signal }) => fetchCampaignList(filters, signal),
		staleTime: 10_000,
		refetchOnWindowFocus: false,
		retry: 1,
	});
}

// ---- formatting helpers ---------------------------------------------------

export function formatCount(n: number): string {
	if (!Number.isFinite(n)) return "0";
	return new Intl.NumberFormat("en-US").format(Math.trunc(n));
}

/**
 * Renders an epoch-ms timestamp as `YYYY-MM-DD HH:mm`. Kept locale-neutral
 * so that QA screenshots compare cleanly across machines.
 */
export function formatTimestamp(ms: number): string {
	if (!Number.isFinite(ms) || ms <= 0) return "—";
	const d = new Date(ms);
	const y = d.getUTCFullYear();
	const mo = String(d.getUTCMonth() + 1).padStart(2, "0");
	const da = String(d.getUTCDate()).padStart(2, "0");
	const hh = String(d.getUTCHours()).padStart(2, "0");
	const mm = String(d.getUTCMinutes()).padStart(2, "0");
	return `${y}-${mo}-${da} ${hh}:${mm}`;
}
